
import { Injectable } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

@Injectable({
  providedIn: 'root',
})
export class ToastHandlerService {
  constructor(private toastr: ToastrService, private router: Router, private confirmDialogService: ConfirmDialogService) { }

  private activeToastIds = new Set<string>();
  private activeToasts = new Map<string, ActiveToast<any>>();

  toastrOptions = {
    timeOut: 10000,
    progressBar: true,
    closeButton: true,
  };

  defaultTitles = {
    success: 'Éxito',
    error: 'Error',
    warning: 'Atención',
    info: 'Información',
  };

  private validateToken(errorData?: any): void {
    const token = localStorage.getItem(
      AuthenticationContants.keyTokenLocalStorage
    );

    const invalidSession =
      errorData?.message ===
      'La sesión ha caducado. Por favor ingrese nuevamente.';

    if (!token || errorData?.error === 'InvalidToken' || invalidSession) {
      this.router.navigate(['/login']);
    } else {
      localStorage.clear();
      this.router.navigate(['/login']);
    }
  }

  private validateTokenExists(): boolean {
    const token = localStorage.getItem(
      AuthenticationContants.keyTokenLocalStorage
    );
    return !!token;
  }

  public showToast(
    type: ToastType,
    message: string,
    title?: string,
    id?: string,
    timeoutOverride?: number
  ): void {
    if (id && this.activeToastIds.has(id)) {
      return;
    }

    const options = {
      ...this.toastrOptions,
      ...(typeof timeoutOverride === 'number'
        ? timeoutOverride === 0
          ? { timeOut: 0, extendedTimeOut: 0, closeButton: true }
          : { timeOut: timeoutOverride }
        : {}),
    };
    // eslint-disable-next-line no-restricted-syntax
    const toastRef = this.toastr[type](message, title || this.defaultTitles[type], options);

    if (id) {
      this.activeToastIds.add(id);
      this.activeToasts.set(id, toastRef);
      if (options.timeOut > 0) {
        setTimeout(() => this.activeToastIds.delete(id), options.timeOut);
      }
    }
  }

  /** Cierra todos los toasts abiertos */
  public clearAllToasts(): void {
    // eslint-disable-next-line no-restricted-syntax
    this.toastr.clear();
    this.activeToastIds.clear();
  }

  /** Cierra un toast específico usando su ID y referencia */
  public clearToastById(id: string): void {
    const toast = this.activeToasts.get(id);
    if (toast) {
      // eslint-disable-next-line no-restricted-syntax
      this.toastr.clear(toast.toastId);
      this.activeToastIds.delete(id);
      this.activeToasts.delete(id);
    }
  }

  public interceptorErrorHandle(
    error: any,
    status: number,
    isBlob: boolean = false
  ): void {
    const errorData = isBlob ? error : error.error;
    switch (status) {
      case 0:
        this.showToast(
          'error',
          'Se presentó un error con el servidor. Por favor comuníquese con la línea de soporte.',
          'ERROR'
        );
        break;
      case 400:
        if (errorData?.errors) {
          const firstError = Object.values(errorData.errors)[0] as string[];
          // eslint-disable-next-line no-restricted-syntax
          this.toastr.error(
            `Se presentó un error: ${firstError[0]}`,
            'ERROR',
            this.toastrOptions
          );
        } else {
          const errorMessage = errorData?.Message ?? errorData?.error ?? 'Solicitud incorrecta.';
          // eslint-disable-next-line no-restricted-syntax
          this.toastr.error(
            `Se presentó un error: ${errorMessage}`,
            'ERROR',
            this.toastrOptions
          );
        }
        break;
      case 401:
        this.showToast(
          'error',
          errorData?.Message ||
          'La sesión ha caducado. Por favor ingrese nuevamente.',
          'ERROR',
          'unauthorized-401',
          0
        );
        this.validateToken(errorData);
        break;
      case 403:
        if (errorData?.Message === 'ActiveSesion') {
          this.confirmDialogService.confirm({
            message: 'El usuario ya se encuentra conectado en otro dispositivo. Si reemplaza la sesión se terminarán los procesos en ejecución. ¿Desea reemplazar la sesión activa?',
            icon: 'pi pi-exclamation-triangle',
          }).then((isConfirmed) => {
            if (isConfirmed) {
              document.getElementById('btnChangeSesion')?.click();
            }
          });
        } else {
          this.showToast(
            'error',
            errorData?.Message ||
            'No se encuentra autorizado para ingresar a esta sección del portal. Por favor comuníquese con la línea de soporte.',
            'ERROR'
          );
        }
        break;
      case 404:
        this.showToast(
          'error',
          'Se presentó un error al buscar el controlador. Por favor comuníquese con la línea de soporte.',
          'ERROR'
        );
        break;
      case 409:
        this.showToast(
          'error',
          'Este registro ya está siendo utilizado en otros procesos. No se puede eliminar.',
          'ERROR'
        );
        break;
      case 500:
        this.showToast(
          'error',
          'Se presentó un error con la base de datos. Por favor comuníquese con la línea de soporte.',
          'ERROR'
        );
        break;
      default:
        if (!this.validateTokenExists()) {
          localStorage.clear();
          this.router.navigate(['/login']);
          return;
        } else {
          this.showToast(
            'error',
            `Error: ${error.message || 'Error desconocido.'}`,
            'ERROR'
          );
        }

        break;
    }
  }
}
